import { useCallback, useEffect, useMemo, useState } from "react";
import {
	getWelcomeBonusCapacity,
	getWelcomeBonusInitialSlots,
	normalizeWelcomeBonusCapacity,
} from "./welcomeBonusCapacity";
import { claimWelcomeBonusRegistration } from "../../data/registrationAgent";

const TEN_MINUTES_MS = 10 * 60 * 1000;

const IMAGE_FALLBACKS = {
	welcome: "/images/character/FGH-CHARACTER-ROLLING-COMMISSION.png",
	waitlist: "/images/character/seo-time.webp",
};

function PromoImage({ alt, className, fallback, src }) {
	return (
		<img
			alt={alt}
			className={className}
			src={src}
			onError={(event) => {
				if (event.currentTarget.src.endsWith(fallback)) return;
				event.currentTarget.src = fallback;
			}}
		/>
	);
}

function formatCountdown(milliseconds) {
	const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;

	return {
		minutes: String(minutes).padStart(2, "0"),
		seconds: String(seconds).padStart(2, "0"),
	};
}

export default function WelcomeBonusModal({ autoOpen = true }) {
	const [state, setState] = useState("closed");
	const [nextAvailableAt, setNextAvailableAt] = useState(null);
	const [remainingTime, setRemainingTime] = useState(0);
	const [checking, setChecking] = useState(false);
	const [capacityError, setCapacityError] = useState("");

	const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

	const openWaitlist = useCallback((capacity = {}) => {
		const normalized = normalizeWelcomeBonusCapacity(capacity);

		const releaseTime =
			normalized.nextAvailableAt || Date.now() + TEN_MINUTES_MS;

		setNextAvailableAt(releaseTime);
		setRemainingTime(Math.max(0, releaseTime - Date.now()));
		setState("waitlist");
	}, []);

	useEffect(() => {
		if (isLoggedIn || !autoOpen) {
			setState("closed");
			return undefined;
		}

		const queryState = new URLSearchParams(window.location.search).get(
			"welcomeBonusModal",
		);

		const validQueryStates = new Set(["welcome", "confirm", "waitlist"]);

		const initialState = validQueryStates.has(queryState)
			? queryState
			: "welcome";

		let cancelled = false;
		const timer = window.setTimeout(async () => {
			if (initialState === "waitlist") {
				openWaitlist();
			} else {
				setState(initialState);
				setChecking(true);
				try {
					const capacity = await getWelcomeBonusCapacity();
					if (!cancelled && !capacity.available) openWaitlist(capacity);
				} catch {
					if (!cancelled) setCapacityError("Unable to check bonus availability. Please try again.");
				} finally {
					if (!cancelled) setChecking(false);
				}
			}
		}, 650);

		return () => {
			cancelled = true;
			window.clearTimeout(timer);
		};
	}, [isLoggedIn, autoOpen, openWaitlist]);

	useEffect(() => {
		const handleCapacityFull = (event) => {
			openWaitlist(event.detail);
		};

		window.addEventListener("fgh:bonus-capacity-full", handleCapacityFull);

		return () => {
			window.removeEventListener("fgh:bonus-capacity-full", handleCapacityFull);
		};
	}, [openWaitlist]);

	useEffect(() => {
		if (state === "closed") return undefined;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [state]);

	useEffect(() => {
		if (state !== "waitlist" || !nextAvailableAt) {
			return undefined;
		}

		let pending = false;
		let cancelled = false;
		let retryAt = 0;
		const updateCountdown = async () => {
			const nextRemainingTime = Math.max(0, nextAvailableAt - Date.now());

			setRemainingTime(nextRemainingTime);

			if (nextRemainingTime === 0 && !pending && Date.now() >= retryAt) {
				pending = true;
				try {
					const capacity = await getWelcomeBonusCapacity();
					if (cancelled) return;
					setCapacityError("");
					if (capacity.available) {
						setState("welcome");
						setNextAvailableAt(null);
					} else openWaitlist(capacity);
				} catch {
					if (!cancelled) setCapacityError("Unable to check slots. Retrying shortly.");
					retryAt = Date.now() + 10000;
				} finally {
					pending = false;
				}
			}
		};

		updateCountdown();

		const interval = window.setInterval(updateCountdown, 1000);

		return () => {
			cancelled = true;
			window.clearInterval(interval);
		};
	}, [nextAvailableAt, state, openWaitlist]);

	useEffect(() => {
		if (state === "closed") return undefined;

		const handleEscape = (event) => {
			if (event.key !== "Escape") return;

			setState((currentState) =>
				currentState === "welcome" ? "confirm" : "closed",
			);
		};

		window.addEventListener("keydown", handleEscape);

		return () => {
			window.removeEventListener("keydown", handleEscape);
		};
	}, [state]);

	const dismiss = () => {
		setState("closed");
	};

	const claimWelcomeBonus = async () => {
		if (checking) return;
		setChecking(true);
		setCapacityError("");
		try {
			const capacity = await getWelcomeBonusCapacity();

			// If full, show waitlist
			if (capacity.available === false || capacity.remainingSlots === 0) {
				openWaitlist(capacity);
				return;
			}

			// Only open registration if slots are available
			if (!claimWelcomeBonusRegistration()) return;
			setState("closed");

			window.dispatchEvent(
				new CustomEvent("fgh:open-registration", {
					detail: {
						source: "welcome-bonus",
					},
				}),
			);
		} catch (error) {
			console.error("Failed to check welcome bonus capacity:", error);

			setCapacityError("Unable to check bonus availability. Please try again.");
		} finally {
			setChecking(false);
		}
	};

	const countdown = useMemo(
		() => formatCountdown(remainingTime),
		[remainingTime],
	);

	if (state === "closed" || isLoggedIn) {
		return null;
	}

	return (
		<div className="fgh-welcome-modal-overlay">
			<section
				aria-labelledby="fgh-welcome-modal-title"
				aria-modal="true"
				className={`fgh-welcome-modal is-${state}`}
				role="dialog"
			>
				<button
					aria-label="Close welcome bonus"
					className="fgh-welcome-modal-close"
					onClick={() =>
						state === "welcome" ? setState("confirm") : dismiss()
					}
					type="button"
				>
					×
				</button>

				{state === "welcome" && (
					<>
						<div className="fgh-welcome-modal-art">
							<PromoImage
								alt="FGH welcome bonus mascot"
								className="fgh-welcome-character"
								fallback={IMAGE_FALLBACKS.welcome}
								src="/images/character/seo-happy.webp"
							/>
						</div>

						<div className="fgh-welcome-modal-copy">
							<span className="fgh-promo-kicker">Get</span>

							<div className="fgh-promo-heading-row">
								<strong className="fgh-promo-amount">₱30</strong>

								<h2 id="fgh-welcome-modal-title">
									Welcome Bonus <small>on FGH</small>
								</h2>
							</div>

							<p>
								Register now on FGH and receive your ₱30 welcome credit
								instantly. No complex extra requirements.
							</p>

							<button
								className="fgh-promo-primary-button"
								onClick={claimWelcomeBonus}
								disabled={checking}
								type="button"
							>
								Claim my ₱30
							</button>

							<div
								className="fgh-promo-benefits"
								aria-label="Welcome bonus benefits"
							>
								<span>No Deposit Required</span>

								<span>Instant ₱30 Credit</span>

								<span>Use on Slots, Games &amp; More</span>
							</div>
						</div>
					</>
				)}

				{state === "confirm" && (
					<>
						<div className="fgh-welcome-modal-art">
							<PromoImage
								alt="FGH welcome bonus mascot"
								className="fgh-welcome-character"
								fallback={IMAGE_FALLBACKS.welcome}
								src="/images/character/seo-happy.webp"
							/>
						</div>

						<div className="fgh-welcome-modal-copy fgh-promo-confirm-copy">
							<span className="fgh-promo-label">Welcome Bonus</span>

							<h2 id="fgh-welcome-modal-title">
								Are you sure you want to leave your <em>₱30 Free Credits?</em>
							</h2>

							<div className="fgh-promo-confirm-benefits">
								<span>
									<b>✓</b>
									No Deposit Required
								</span>

								<span>
									<b>₱</b>
									Instant Credit After Signup
								</span>

								<span>
									<b>▣</b>
									Use on Slots, Games &amp; More
								</span>
							</div>

							<button
								className="fgh-promo-primary-button"
								onClick={claimWelcomeBonus}
								disabled={checking}
								type="button"
							>
								Yes, claim my ₱30
							</button>

							<button
								className="fgh-promo-secondary-button"
								onClick={dismiss}
								type="button"
							>
								No thanks, maybe later
							</button>
						</div>
					</>
				)}

				{state === "waitlist" && (
					<>
						<div className="fgh-welcome-modal-art">
							<PromoImage
								alt="FGH mascot waiting for registration slots"
								className="fgh-waitlist-character"
								fallback={IMAGE_FALLBACKS.waitlist}
								src="/images/character/seo-time.webp"
							/>
						</div>

						<div className="fgh-welcome-modal-copy fgh-waitlist-copy">
							<span className="fgh-promo-label is-warning">Slots are full</span>

							<h2 id="fgh-welcome-modal-title">
								All welcome bonus slots are currently taken.
							</h2>

							<p>{getWelcomeBonusInitialSlots()} welcome bonuses start each day. After they are claimed, 10 more slots open after each 10-minute cooldown.</p>

							<div className="fgh-waitlist-timer" aria-live="polite">
								<span>Next slot opens in</span>

								<strong>
									{countdown.minutes}:{countdown.seconds}
								</strong>

								<small>Minutes&nbsp;&nbsp;&nbsp;&nbsp; Seconds</small>
							</div>

							<button
								className="fgh-promo-secondary-button is-light"
								onClick={dismiss}
								type="button"
							>
								Close
							</button>

							<small className="fgh-promo-privacy">
								Keep this window open to automatically check when slots reopen.
							</small>
						</div>
					</>
				)}

				{capacityError && <p role="alert">{capacityError}</p>}
				<footer>New members only · 21+ only · Terms apply.</footer>
			</section>
		</div>
	);
}
